import type { SupportedLocale } from "@/lib/locales";
import { apiClient, isApiClientError } from "@/lib/api";
import { ProfileType } from "@/types";

export type PartnerNotificationChannel = "WHATSAPP" | "EMAIL";
export type LegalProfileRuleset = "IT" | "ES" | "GENERIC";
export type LegalFieldName =
  | "first_name"
  | "last_name"
  | "birth_place"
  | "birth_date"
  | "fiscal_code"
  | "registered_street"
  | "registered_postal_code"
  | "registered_city"
  | "registered_province"
  | "registered_country"
  | "document_type"
  | "document_number"
  | "document_issuer"
  | "document_issue_date"
  | "document_expiry_date"
  | "company_name"
  | "vat_number"
  | "pec"
  | "legal_rep_name"
  | "contact_phone"
  | "contact_email"
  | "partner_notification_channel"
  | "preferred_language"
  | "iban"
  | "account_holder";

export interface LegalFieldPolicy {
  ruleset: LegalProfileRuleset;
  excluded: LegalFieldName[];
  optional: LegalFieldName[];
}

/**
 * Canonical `/users/{user_id}/legal-info` payload.
 * String columns match backend ORM ``Mapped[str]`` (empty string, never null).
 * Date fields remain nullable.
 */
export interface UserLegalInfo {
  id: string;
  user_id: string;
  profile_type: ProfileType;
  /** Assigned at account creation; read-only in the portal. */
  region_id: string;
  /** Payment region code when the region relationship is loaded (read-only). */
  region_code: string;

  first_name: string;
  last_name: string;
  birth_date: string | null;
  birth_place: string;

  fiscal_code: string;

  registered_street: string;
  registered_postal_code: string;
  registered_city: string;
  registered_province: string;
  registered_country: string;

  document_type: string;
  document_number: string;
  document_issuer: string;
  document_issue_date: string | null;
  document_expiry_date: string | null;

  company_name: string;
  vat_number: string;
  pec: string;

  legal_rep_name: string;

  contact_phone: string;
  contact_phone_verified: boolean;
  contact_email: string;
  contact_email_verified: boolean;
  partner_notification_channel: PartnerNotificationChannel;
  preferred_language: SupportedLocale;

  iban: string;
  account_holder: string;

  profile_completed: boolean;
  legal_profile_ruleset: LegalProfileRuleset;
  field_policy: LegalFieldPolicy;
}

export type UserLegalInfoUpdate = Partial<
  Omit<
    UserLegalInfo,
    | "id"
    | "user_id"
    | "profile_completed"
    | "region_id"
    | "region_code"
    | "legal_profile_ruleset"
    | "field_policy"
    | "contact_phone_verified"
    | "contact_email_verified"
  >
>;

interface ValidationError {
  field: string;
  message: string;
}

function parseBackendErrors(error: unknown): ValidationError[] {
  const data = isApiClientError(error)
    ? (error.response?.data as
        | {
            detail?: Array<{ loc?: string[]; msg?: string }>;
            details?: Array<{ loc?: string[]; msg?: string }>;
          }
        | undefined)
    : undefined;
  const details = data?.details ?? data?.detail;

  if (!details || !Array.isArray(details)) {
    return [];
  }

  return details.map((detail) => {
    const fieldPath = detail.loc?.slice(1).join(".") || "root";
    return {
      field: fieldPath,
      message: detail.msg || "Invalid value",
    };
  });
}

export class ProfileValidationError extends Error {
  public errors: ValidationError[];

  constructor(message: string, errors: ValidationError[]) {
    super(message);
    this.name = "ProfileValidationError";
    this.errors = errors;
  }
}

/** Shared React Query key for the current user's legal profile. */
export function legalInfoQueryKey(userId: string) {
  return ["legal-info", userId] as const;
}

/** User ID is the canonical authenticated `users.id` value. */
export const profileService = {
  getProfile: async (userId: string): Promise<UserLegalInfo> => {
    const response = await apiClient.get<UserLegalInfo>(
      `/users/${userId}/legal-info`,
    );
    return response.data;
  },

  updateProfile: async (
    userId: string,
    data: UserLegalInfoUpdate,
  ): Promise<UserLegalInfo> => {
    try {
      const response = await apiClient.put<UserLegalInfo>(
        `/users/${userId}/legal-info`,
        data,
      );
      return response.data;
    } catch (error: unknown) {
      if (isApiClientError(error) && error.response?.status === 422) {
        const data = error.response.data as { message?: string } | undefined;
        const errors = parseBackendErrors(error);
        const message = data?.message || "Please check the highlighted fields.";
        throw new ProfileValidationError(message, errors);
      }
      throw error;
    }
  },

  changeProfileType: async (
    userId: string,
    newType: ProfileType,
    confirm: boolean = false,
  ): Promise<UserLegalInfo> => {
    const response = await apiClient.post<UserLegalInfo>(
      `/users/${userId}/legal-info/change-type`,
      { new_type: newType, confirm },
    );
    return response.data;
  },

  sendPhoneVerification: async (userId: string): Promise<void> => {
    await apiClient.post(`/users/${userId}/phone/verification/send`);
  },

  confirmPhoneVerification: async (
    userId: string,
    code: string,
  ): Promise<UserLegalInfo> => {
    const response = await apiClient.post<UserLegalInfo>(
      `/users/${userId}/phone/verification/confirm`,
      { code },
    );
    return response.data;
  },

  sendEmailVerification: async (userId: string): Promise<void> => {
    await apiClient.post(`/users/${userId}/email/verification/send`);
  },
};
